/**
 * Pomocniki do drag-&-drop plików/katalogów.
 *
 * Współdzielone między {@link BulkClassificationModal} (bulk-import opracowania)
 * a {@link Drive} (wgrywanie do archiwum) — w obu przypadkach chcemy, żeby user mógł
 * upuścić CAŁY KATALOG (nie tylko pojedyncze pliki) i żebyśmy rekursywnie zebrali
 * wszystkie pliki z drzewa.
 *
 * Mechanizm: `DataTransferItem.webkitGetAsEntry()` + `FileSystemDirectoryEntry` —
 * niestandardowe API, ale wspierane we wszystkich nowoczesnych przeglądarkach
 * (Chrome, Firefox, Safari, Edge). Fallback na `dataTransfer.files` dla przeglądarek
 * bez tego API (rzadkość — głównie starsze Safari).
 */

/**
 * Rekursywnie czyta plik lub katalog do płaskiej listy `File[]`.
 * @param onFile — opcjonalny callback wywoływany po dodaniu każdego pliku do listy.
 *                 Pozwala UI na inkrementalne pokazywanie postępu skanowania.
 */
export async function readEntry(entry: FileSystemEntry, onFile?: () => void): Promise<File[]> {
  if (entry.isFile) {
    return new Promise<File[]>((resolve, reject) => {
      (entry as FileSystemFileEntry).file(
        f => { onFile?.(); resolve([f]); },
        e => reject(e),
      );
    });
  }
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const out: File[] = [];
    // readEntries() zwraca pliki w batch'ach (Chromium tnie po ~100 wpisów),
    // więc czytamy w pętli aż dostaniemy pustą tablicę.
    const readBatch = (): Promise<FileSystemEntry[]> => new Promise((res, rej) => {
      reader.readEntries(es => res(Array.from(es)), e => rej(e));
    });
    while (true) {
      const batch = await readBatch();
      if (batch.length === 0) break;
      for (const child of batch) out.push(...await readEntry(child, onFile));
    }
    return out;
  }
  return [];
}

/**
 * Z `DataTransfer` wyciąga pełną listę plików — łącznie z plikami w upuszczonych
 * katalogach (rekursywnie). Działa zarówno dla drag&drop pojedynczych plików, jak
 * i całego folderu.
 *
 * UWAGA: po pierwszym `await` przeglądarka zwalnia DataTransferItemList, więc
 * MUSIMY synchronicznie zebrać wszystkie `FileSystemEntry` przed jakimkolwiek
 * awaitem. Inaczej drugie wywołanie `webkitGetAsEntry()` zwraca `null` i drop
 * po cichu „nic nie robi".
 *
 * @param onProgress — opcjonalny callback wywoływany w trakcie skanowania drzewa
 *                     (po dodaniu każdego pliku) z aktualną liczbą znalezionych
 *                     plików. Pozwala UI pokazać licznik „Skanuję folder… N plików".
 */
export async function readDroppedFiles(
  dt: DataTransfer,
  onProgress?: (filesSoFar: number) => void,
): Promise<File[]> {
  // ── KROK 1: synchroniczny snapshot — żadnych awaitów! ───────────────────────
  const entries: FileSystemEntry[] = [];
  const directFiles: File[] = [];
  const items = dt.items;
  if (items && items.length > 0) {
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind !== 'file') continue;
      const getEntry = (it as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry;
      const entry = typeof getEntry === 'function' ? getEntry.call(it) : null;
      if (entry) {
        entries.push(entry);
      } else {
        // Przeglądarka bez webkitGetAsEntry albo item to nie-folder/plik (np. string).
        const f = it.getAsFile();
        if (f) directFiles.push(f);
      }
    }
  }
  // Fallback gdyby items[] były puste — Safari w niektórych wersjach.
  const filesFallback = (entries.length === 0 && directFiles.length === 0 && dt.files?.length)
    ? Array.from(dt.files)
    : [];

  // ── KROK 2: dopiero teraz await — entries to niezależne handle do FS ────────
  const collected: File[] = [...directFiles];
  let scannedCount = collected.length;
  if (scannedCount > 0) onProgress?.(scannedCount);
  // Throttling raportowania — dla folderu z 5000 plików nie chcemy 5000 setState'ów.
  // Wywołujemy callback tylko gdy upłynęło ≥80ms od ostatniego.
  let lastReportTs = 0;
  const reportFile = () => {
    scannedCount++;
    const now = Date.now();
    if (now - lastReportTs >= 80) {
      lastReportTs = now;
      onProgress?.(scannedCount);
    }
  };
  for (const entry of entries) {
    try { collected.push(...await readEntry(entry, reportFile)); } catch { /* best-effort */ }
  }
  // Finalna aktualizacja po skończonym skanowaniu.
  if (scannedCount > 0) onProgress?.(scannedCount);
  if (collected.length === 0) collected.push(...filesFallback);
  return collected;
}

/**
 * Heurystyka filtrowania śmieci systemowych z folderu (macOS/Windows). Wywołuj na
 * wyniku z {@link readDroppedFiles} przed uploadem, żeby user nie widział „✗ .DS_Store"
 * w wynikach.
 */
export function isSystemJunk(name: string): boolean {
  if (!name) return false;
  if (name === '.DS_Store') return true;
  if (name === '.localized') return true;
  if (name === 'Thumbs.db') return true;
  if (name === 'desktop.ini') return true;
  if (name.startsWith('._')) return true;       // macOS resource forks (AppleDouble)
  return false;
}
